import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import aulaService from '../services/AulaService';
import aulaDocenteService from '../services/AulaDocenteService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import LoadingSpinner from './common/LoadingSpinner';
import SearchableSelect from './common/SearchableSelect';
import ProfessorMenu from './common/menus/ProfessorMenu';
import AdminMenu from './common/menus/AdminMenu';
import { hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';

const normalizeListResponse = (response) => {
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.profesores)) return response.profesores;
  if (Array.isArray(response?.Profesores)) return response.Profesores;
  return Array.isArray(response) ? response : [];
};

const normalizeIdList = (value) => (
  Array.isArray(value)
    ? value.map((item) => (item == null ? '' : String(item))).filter(Boolean)
    : []
);

const getUserId = (user) => String(user?.id || user?.Id || user?.userId || user?.UserId || '').trim();

const AulaDocenteForm = () => {
  const navigate = useNavigate();
  const { aulaId } = useParams();
  const { user, logout } = useAuth();
  const isEdit = Boolean(aulaId);

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const roleName = normalizeRoleName(user);
  const isAdminUser = roleName.includes('admin');
  const isGeneralAdmin = isAdminUser && !isAdminColegio(user);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => isFeatureEnabled(featureFlags, 'ModuloAlumnos'), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT');
  }, [featureFlags]);
  const canSubmit = isAdminUser || isEdit || Boolean(user?.permiteCrearAula);

  const [profesores, setProfesores] = useState([]);
  const [classicAssistants, setClassicAssistants] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    nicknameUrl: '',
    docenteIds: [],
    idsAsistentes: [],
    cicloLectivo: currentYear,
    idColegio: activeColegioId || ''
  });

  useEffect(() => {
    if (!activeColegioId) return;

    let cancelled = false;
    const fetchData = async () => {
      setIsLoadingData(true);
      setMessage('');
      setMessageType('');

      try {
        const requests = [
          aulaDocenteService.getProfesoresParaAulaDocente(activeColegioId, { start: 0, end: 9999, includeTotal: true })
        ];

        if (hasAgentesClasicos) {
          requests.push(
            isAdminUser
              ? aulaService.getAsistentesGptClasicosParaAula({ idColegio: activeColegioId })
              : aulaService.getMisAsistentesGptClasicosParaAula({ idColegio: activeColegioId })
          );
        }

        if (isEdit) {
          requests.push(aulaDocenteService.getAulaDocenteById(aulaId));
        }

        const results = await Promise.all(requests);
        if (cancelled) return;

        const profesoresRes = results[0];
        const assistantsRes = hasAgentesClasicos ? results[1] : [];
        const aulaRes = isEdit ? results[hasAgentesClasicos ? 2 : 1] : null;
        const profesoresList = normalizeListResponse(profesoresRes);
        setProfesores(profesoresList);
        setClassicAssistants(Array.isArray(assistantsRes) ? assistantsRes : []);

        if (isEdit && aulaRes) {
          setForm({
            nombre: aulaRes.nombre || aulaRes.Nombre || '',
            descripcion: aulaRes.descripcion || aulaRes.Descripcion || '',
            nicknameUrl: aulaRes.nicknameUrl || aulaRes.NicknameUrl || '',
            docenteIds: normalizeIdList(aulaRes.docenteIds || aulaRes.DocenteIds),
            idsAsistentes: normalizeIdList(aulaRes.idsAsistentes || aulaRes.IdsAsistentes),
            cicloLectivo: aulaRes.cicloLectivo || aulaRes.CicloLectivo || currentYear,
            idColegio: aulaRes.idColegio || aulaRes.IdColegio || activeColegioId || ''
          });
        } else {
          const currentUserId = getUserId(user);
          const belongsToSchool = currentUserId && profesoresList.some((profesor) => String(profesor?.id || profesor?.Id || '') === currentUserId);
          setForm((prev) => ({
            ...prev,
            idColegio: prev.idColegio || activeColegioId,
            docenteIds: !isAdminUser && belongsToSchool && prev.docenteIds.length === 0
              ? [currentUserId]
              : prev.docenteIds
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setMessage(error.message || 'No pudimos cargar los datos del aula docente.');
          setMessageType('error');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingData(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [activeColegioId, aulaId, currentYear, hasAgentesClasicos, isAdminUser, isEdit, user]);

  const profesorOptions = useMemo(() => (
    profesores
      .map((profesor) => {
        const id = String(profesor?.id || profesor?.Id || '');
        const displayName = profesor?.displayName || profesor?.DisplayName || profesor?.nombre || profesor?.Nombre || '';
        const email = profesor?.email || profesor?.Email || '';
        const label = displayName || email || 'Docente';

        return {
          value: id,
          label,
          secondary: email && email !== label ? email : '',
          searchText: `${label} ${email}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [profesores]);

  const assistantOptions = useMemo(() => (
    classicAssistants
      .map((assistant) => {
        const id = String(assistant?.idOpenAIAssistant || assistant?.IdOpenAIAssistant || '');
        const nombreVisible = assistant?.nombreVisible || assistant?.NombreVisible || assistant?.nombre || assistant?.Nombre || 'Asistente IA';
        const nombreMateria = assistant?.nombreMateria || assistant?.NombreMateria || '';
        const nombreArea = assistant?.nombreArea || assistant?.NombreArea || '';
        const secondary = [nombreMateria, nombreArea].filter(Boolean).join(' · ');

        return {
          value: id,
          label: nombreVisible,
          secondary,
          searchText: `${nombreVisible} ${nombreMateria} ${nombreArea}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [classicAssistants]);

  const allProfesorIds = useMemo(() => (
    profesorOptions.map((option) => option.value).filter(Boolean)
  ), [profesorOptions]);

  const selectedProfesorIds = useMemo(() => normalizeIdList(form.docenteIds), [form.docenteIds]);

  const allProfesoresSelected = useMemo(() => (
    allProfesorIds.length > 0 && allProfesorIds.every((id) => selectedProfesorIds.includes(id))
  ), [allProfesorIds, selectedProfesorIds]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocentesChange = (value) => {
    handleChange('docenteIds', normalizeIdList(value));
  };

  const handleToggleAllProfesores = (checked) => {
    handleChange('docenteIds', checked ? allProfesorIds : []);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const docenteIds = normalizeIdList(form.docenteIds);
    if (!canSubmit) {
      setMessage('No tienes permisos para crear aulas docentes.');
      setMessageType('error');
      return;
    }
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del aula docente.');
      setMessageType('error');
      return;
    }
    if (docenteIds.length === 0) {
      setMessage('Selecciona al menos un docente.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage(isEdit ? 'Guardando cambios...' : 'Creando aula docente...');
    setMessageType('info');

    const payload = {
      nombre: form.nombre,
      descripcion: form.descripcion,
      nicknameUrl: form.nicknameUrl,
      codigoSuscripcion: '',
      docenteIds,
      idsAsistentes: normalizeIdList(form.idsAsistentes),
      cicloLectivo: Number(form.cicloLectivo) || currentYear,
      idColegio: form.idColegio
    };

    try {
      if (isEdit) {
        await aulaDocenteService.updateAulaDocente(aulaId, payload);
        setMessage('Aula docente actualizada correctamente.');
      } else {
        await aulaDocenteService.createAulaDocente(payload);
        setMessage('Aula docente creada correctamente.');
      }
      setMessageType('success');
      setTimeout(() => navigate('/aulas-docentes', { replace: true }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos guardar el aula docente.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfessorNavigate = (item) => {
    if (item?.path) {
      navigate(item.path);
    }
  };

  const handleAdminNavigate = (item) => {
    if (!item) return;
    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
      return;
    }
    if (item.id === 'aulas-docentes') {
      navigate('/aulas-docentes');
      return;
    }
    if (item.id === 'agentes-clasicos-gpt') {
      navigate('/mis-agentes-clasicos-gpt');
      return;
    }
    navigate('/admin-dashboard', { state: { tab: item.id } });
  };

  const handleBackToSelectColegio = () => {
    ColegioService.clearColegioData();
    navigate('/admin/select-colegio', { replace: true });
  };

  const title = isEdit ? 'Editar aula docente' : 'Alta de aula docente';

  return (
    <div className={`new-dashboard ${isAdminUser ? 'admin-mode' : ''}`}>
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className={`fas ${isAdminUser ? 'fa-university' : 'fa-graduation-cap'}`}></i>
            <span>{isAdminUser ? 'Admin' : 'aluxIA'}</span>
          </div>
        </div>

        {isAdminUser ? (
          <AdminMenu
            activeId="aulas-docentes"
            user={user}
            featureFlags={featureFlags}
            onNavigate={handleAdminNavigate}
          />
        ) : (
          <ProfessorMenu
            activeId="aulas-docentes"
            hasOpenAIChats={hasOpenAIChats}
            hasAgentesClasicos={hasAgentesClasicos}
            hasModuloAlumnos={hasModuloAlumnos}
            onNavigate={handleProfessorNavigate}
          />
        )}

        <div className="sidebar-footer">
          {isAdminUser && <ActiveColegioBadge />}
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className={`fas ${isAdminUser ? 'fa-user-shield' : 'fa-user'}`}></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.roleName || user?.role || (isAdminColegio(user) ? 'Admin colegio' : 'Profesor')}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-chalkboard-teacher"></i>
              {title}
            </h1>
            <p className="page-subtitle">{isEdit ? 'Actualiza' : 'Crea'} un aula docente para {activeColegioName}.</p>
          </div>

          <div className="header-right">
            {!isAdminUser && <SolicitudesDropdown />}
            <ActiveColegioBadge />
            <UserSessionMenu
              user={user}
              onLogout={logout}
              showStandaloneLogout={false}
              extraItems={isGeneralAdmin ? [{ key: 'switch-school', label: 'Cambiar colegio', icon: 'fas fa-exchange-alt', onClick: handleBackToSelectColegio }] : []}
              userFallbackLabel={isAdminUser ? 'Administrador' : 'Profesor'}
            />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-chalkboard-teacher" />
                <span>Datos del aula docente</span>
              </div>
            </div>
            <div className="card-content">
              {isLoadingData && (
                <div className="data-table-loading">
                  <LoadingSpinner size="small" message="Cargando datos..." />
                </div>
              )}

              {!canSubmit && !isLoadingData && (
                <p style={{ marginTop: 0, color: '#b91c1c' }}>
                  No tienes permisos para crear aulas docentes. Contacta al administrador.
                </p>
              )}

              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(event) => handleChange('nombre', event.target.value)}
                    placeholder="Aula docentes"
                    disabled={isLoadingData || !canSubmit}
                  />
                </div>

                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(event) => handleChange('descripcion', event.target.value)}
                    placeholder="Descripción del aula docente"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    disabled={isLoadingData || !canSubmit}
                  />
                </div>

                <div className="form-field">
                  <label>Nickname URL</label>
                  <input
                    type="text"
                    value={form.nicknameUrl}
                    onChange={(event) => handleChange('nicknameUrl', event.target.value)}
                    placeholder="Ej: aula-docentes-2026"
                    disabled={isLoadingData || !canSubmit}
                  />
                </div>

                <div className="form-field">
                  <label>Ciclo lectivo</label>
                  <input
                    type="number"
                    value={form.cicloLectivo}
                    onChange={(event) => handleChange('cicloLectivo', event.target.value)}
                    min="2000"
                    max="2100"
                    disabled={isLoadingData || !canSubmit}
                  />
                </div>

                <div className="form-field">
                  <label>Docentes</label>
                  <SearchableSelect
                    value={form.docenteIds}
                    onChange={handleDocentesChange}
                    options={profesorOptions}
                    placeholder="Busca docentes por nombre o email"
                    emptyText="No hay docentes activos para este colegio."
                    noResultsText="No encontramos docentes con esa búsqueda."
                    disabled={isLoadingData || !canSubmit}
                    multiple
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={allProfesoresSelected}
                        onChange={(event) => handleToggleAllProfesores(event.target.checked)}
                        disabled={isLoadingData || !canSubmit || allProfesorIds.length === 0}
                      />
                      Todos los profesores
                    </label>
                    <small style={{ color: '#64748b' }}>
                      Se seleccionarán automáticamente los {allProfesorIds.length} docentes activos del colegio.
                    </small>
                  </div>
                </div>

                {hasAgentesClasicos && (
                  <div className="form-field">
                    <label>Asistentes IA del aula docente</label>
                    <SearchableSelect
                      value={form.idsAsistentes}
                      onChange={(value) => handleChange('idsAsistentes', normalizeIdList(value))}
                      options={assistantOptions}
                      placeholder="Busca asistentes IA por nombre, materia o área"
                      emptyText="No hay asistentes IA finalizados disponibles."
                      noResultsText="No encontramos asistentes IA con esa búsqueda."
                      disabled={isLoadingData || !canSubmit}
                      multiple
                    />
                  </div>
                )}

                {message && <div className={`form-message ${messageType}`}>{message}</div>}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/aulas-docentes')}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting || isLoadingData || !canSubmit}>
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {isEdit ? 'Guardar cambios' : 'Crear aula docente'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AulaDocenteForm;
